import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { localStorageMethod } from 'src/common/config/FunctionHelper';
import {
  setReduxFilteredLocationsData,
  setReduxLocationsData,
  setSelectedBusinessName,
  setSelectedLocationId,
  setSelectedAccountId,
  setLocationListResponse
} from 'src/redux/localSeo';
import { useGetGBPLocations } from '../../orbitAi/hooks/useGetGBPLocations';

interface UseGBPLocationsOptions {
  /** Whether the Local SEO feature is currently enabled. */
  isLocalSeoEnabled: boolean;
  /** Whether the Review Automation feature is currently enabled. */
  isReviewAutomationEnabled: boolean;
  /** Current length of the Redux filtered-locations array (used as an effect guard). */
  reduxFilteredLocationsDataLength: number;
  /** Currently selected business name from Redux. */
  selectedBusinessName: string;
  /**
   * Called once after locations are successfully fetched,
   * so the parent can apply a default for review-automation business.
   */
  onLocationsLoaded?: (locations: any[]) => void;
}

/**
 * Manages fetching Google Business Profile locations from the API using React Query,
 * dispatching results to Redux, and selecting a default location.
 */
export const useGBPLocations = ({
  isLocalSeoEnabled,
  isReviewAutomationEnabled,
  reduxFilteredLocationsDataLength,
  selectedBusinessName,
  onLocationsLoaded
}: UseGBPLocationsOptions) => {
  const dispatch = useDispatch();

  // Keep the callback in a ref so it's always fresh but never triggers re-runs
  const onLocationsLoadedRef = useRef(onLocationsLoaded);
  useEffect(() => {
    onLocationsLoadedRef.current = onLocationsLoaded;
  });

  const applyLocation = (loc: any) => {
    dispatch(setSelectedBusinessName(loc.title || loc.name));
    dispatch(setSelectedLocationId(loc.name));
    dispatch(setSelectedAccountId(loc.accountId));
  };

  const shouldFetch = reduxFilteredLocationsDataLength === 0;
  const squareSpaceSiteId = localStorageMethod('get', 'squareSpaceSiteId');

  const { data: response, isLoading: locationsLoading, isError: locationsError, refetch } = useGetGBPLocations({
    isConnecting: false,
    selectedAccountId: null,
    squareSpaceSiteId: squareSpaceSiteId ?? null
  }, {
    enabled: shouldFetch
  });

  useEffect(() => {
    if (shouldFetch && response) {
      dispatch(setLocationListResponse(response));
      if (response?.status === 'success' && response?.isGBPConnected) {
        const locations = response.locations || [];
        const selectedLocations = locations.filter(
          (loc: any) => loc?.selected === true && loc?.temp !== true
        );
        const finalLocs = selectedLocations.length > 0 ? selectedLocations : locations;

        dispatch(setReduxFilteredLocationsData(finalLocs));
        dispatch(setReduxLocationsData(locations));

        if (finalLocs.length > 0 && !selectedBusinessName) {
          applyLocation(finalLocs[0]);
        }
        if (finalLocs.length > 0) {
          onLocationsLoadedRef.current?.(finalLocs);
        }
      }
    }
  }, [response, shouldFetch, selectedBusinessName, dispatch]);

  return {
    locationsLoading,
    locationsError,
    refetch,
    applyLocation
  };
};
